// App-shell — the persistent application chrome.
//
// Before this, App.tsx mounted routed pages with NO chrome: a signed-in
// user could only move between surfaces by hand-typing URLs, which made
// the whole app read as a rough test environment. The shell gives the
// app hallways: one sticky top bar with role-aware navigation + an
// account menu, wrapping every authenticated surface via a react-router
// layout route (see App.tsx). The <Outlet /> renders the routed page
// below the bar.
//
// Role-aware, off useAccountMe():
//   * signed in            → Feed / Shop / Marketplace / Collection
//                            (Wallet is reached via the balance pill, not nav)
//   * + creator_status===approved → + Creator (dashboard)
//   * + is_admin           → + Admin
//   * signed out           → wordmark + "Sign in" (auth-optional pages
//                            like /marketplace 401 gracefully and render
//                            their own sign-in prompt below the bar)
//
// Home is /feed for every role per the confirmed product vision (every
// signup is identical; you apply-as-creator afterward).

import { useEffect, useRef, useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { BalanceDisplay, SiteFooter } from "@greenhold/components";

import {
  hasCreatorPageAccess,
  useAccountMe,
  type AccountMeEnvelope,
} from "../features/admin/useAccountMe";
import { useLogout } from "../features/auth/useLogout";
import { useFreeClaim } from "../features/free-claim/useFreeClaim";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { attachRequestIdToScope } from "../lib/api-client";
import { RouteErrorBoundary } from "./RouteErrorBoundary";

type NavItem = { to: string; label: string };

const BASE_NAV: NavItem[] = [
  { to: "/feed", label: "Feed" },
  // Shop = primary market (buy dossiers from the platform); Marketplace =
  // secondary market (buy from other players). Primary before secondary.
  { to: "/shop", label: "Shop" },
  { to: "/marketplace", label: "Marketplace" },
  // Vault route stays `/vault` (server-provided redirect URLs + share
  // deep-links depend on it); only the user-facing label is "Collection".
  { to: "/vault", label: "Collection" },
  // Wallet is no longer a nav item — the balance pill in the shell chrome
  // (ShellBalancePill) is the affordance that navigates to /account/wallet.
];

export function AppShell() {
  const session = useAccountMe();
  const me = session.data?.data;
  const signedIn = Boolean(me?.user_id);
  const location = useLocation();

  // Reset scroll to the top on navigation — react-router doesn't do this by
  // default, which made moving from a long page (Wallet) to a short one
  // (Vault) read as "nothing happened".
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* jsdom / non-browser: no-op */
    }
  }, [location.pathname]);

  // Shop-nav "free claim available" dot — reads the SAME ["free-claim"]
  // cache the /shop page uses (30s stale), gated to signed-in chrome so it
  // never fires for signed-out visitors. The dot lights ONLY when the user
  // has an unclaimed weekly free dossier available to mint (state ===
  // "claimable"); once they claim it (state "claimed") the dot goes dark.
  const freeClaim = useFreeClaim({ enabled: signedIn });
  const freeClaimAvailable = freeClaim.data?.data.state === "claimable";

  const navItems: NavItem[] = signedIn ? [...BASE_NAV] : [];
  // Approved AND former creators (withdrew consent) get the Creator nav
  // item — former creators keep read access to their stats + remaining
  // portfolio withdrawals.
  if (signedIn && hasCreatorPageAccess(me)) {
    navItems.push({ to: "/account/creator-dashboard", label: "Creator" });
  }
  if (signedIn && me?.is_admin) {
    navItems.push({ to: "/admin/applications", label: "Admin" });
  }

  return (
    <div className="min-h-screen bg-brand-forest-900 text-brand-paper-100">
      <header className="sticky top-0 z-40 border-b border-brand-gold-500/20 bg-brand-forest-900/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-6 px-6">
          <Link
            to={signedIn ? "/feed" : "/"}
            className="shrink-0 font-heading text-xl tracking-wide text-brand-gold-300"
            data-testid="shell-wordmark"
          >
            Greenhold
          </Link>

          {signedIn ? (
            <nav
              className="flex flex-1 items-center gap-1 overflow-x-auto"
              aria-label="Primary"
            >
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    [
                      "relative whitespace-nowrap rounded px-3 py-1.5 font-body text-sm transition-colors",
                      // NAV LAW: exact match only (`end`), one active item;
                      // active = inset-bottom indicator + weight, not a bg swap.
                      isActive
                        ? "font-semibold text-brand-gold-300 shadow-[inset_0_-2px_0_0_#cea33d]"
                        : "text-brand-paper-100/70 hover:bg-brand-forest-800/40 hover:text-brand-paper-100",
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      {/* Gold "free claim available" dot — on every page EXCEPT
                          Shop itself, when the user's welcome dossier is still
                          unclaimed. */}
                      {item.to === "/shop" && freeClaimAvailable && !isActive ? (
                        <>
                          <span
                            aria-hidden="true"
                            className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-brand-gold-500 shadow-[0_0_6px_rgba(195,146,53,0.7)]"
                          />
                          <span className="sr-only"> (free claim available)</span>
                        </>
                      ) : null}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          ) : (
            <div className="flex-1" />
          )}

          <div className="flex shrink-0 items-center gap-3">
            {signedIn ? (
              <>
                {/* Story 7.6 (AC 2) — wallet balance pill in the top bar.
                    Compact variant per Story 1.15; Top-up routes to the
                    wallet page which hosts the real TopUpModal flow. */}
                <ShellBalancePill />
                <NotificationBell />
                <AccountMenu
                  email={me?.email ?? ""}
                  displayName={me?.handle ?? undefined}
                  creatorStatus={me?.creator_status}
                />
              </>
            ) : (
              <Link
                to="/"
                className="rounded px-3 py-1.5 font-body text-sm text-brand-gold-300 hover:brightness-110"
                data-testid="shell-sign-in"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <RouteErrorBoundary key={location.pathname}>
        <Outlet />
      </RouteErrorBoundary>

      {/* mt-0 overrides SiteFooter's default mt-16. The shell root is
          brand-forest-900 while the surfaces inside it (Feed, Shop,
          Marketplace, admin) paint their own light brand-paper-200 ground
          that stops at their content edge — so a 4rem top margin rendered
          as a strip of unexplained forest between the light page and the
          footer, with the gold hairline floating in the middle of it.
          Zeroed, the light page butts straight onto the dark footer band
          and the hairline lands on the seam it exists to draw. */}
      <SiteFooter className="mt-0" />
    </div>
  );
}

function ShellBalancePill() {
  const navigate = useNavigate();
  return (
    <BalanceDisplay
      variant="compact"
      onBalanceClick={() => navigate("/account/wallet")}
      onTopupClick={() => navigate("/account/wallet")}
      onResponse={attachRequestIdToScope}
      className="hidden sm:inline-flex"
    />
  );
}

function AccountMenu({
  email,
  displayName,
  creatorStatus,
}: {
  email: string;
  /** Preferred source for the avatar initial (user handle). Falls back to
   *  the email's first letter when unset. */
  displayName?: string;
  creatorStatus?: "none" | "applied" | "approved" | "rejected" | "suspended";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useLogout();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleSignOut = async () => {
    try {
      await logout.mutateAsync();
    } catch {
      // Even if the network call fails, drop the client session state and
      // bounce to the landing — a stale cookie is less harmful than a UI
      // stuck in a signed-in-but-broken state.
    }
    // SECURITY: wipe the ENTIRE query cache, not just account-me. Every
    // user-scoped query (creator application status/draft, wallet,
    // collection…) would otherwise survive in memory into the next login
    // on the same tab and leak the previous user's data — e.g. a new user
    // seeing the prior user's pending creator application. The immediate
    // setQueryData runs synchronously after clear(), so the still-mounted
    // useAccountMe observer reads null (fresh) instead of refetching
    // /api/v1/account/me with no cookie (which 401s twice). Guards +
    // AppShell already treat an absent `me` as signed-out.
    queryClient.clear();
    queryClient.setQueryData<AccountMeEnvelope | null>(["account-me"], null);
    setOpen(false);
    navigate("/", { replace: true });
    toast.success("Signed out.");
  };

  const initial =
    (displayName?.trim().charAt(0) || email.trim().charAt(0)).toUpperCase() ||
    "?";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold-500"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={email ? `Account menu (${email})` : "Account menu"}
        data-testid="account-menu-trigger"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold-500/20 font-heading text-sm text-brand-gold-300 transition-colors hover:bg-brand-gold-500/30">
          {initial}
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 overflow-hidden rounded-md border border-brand-gold-500/20 bg-brand-forest-800 py-1 shadow-lg"
          data-testid="account-menu"
        >
          {/* Apply-as-creator entry — every signup is identical; you apply
              afterward. Hidden once approved (the Creator nav takes over). */}
          {creatorStatus && creatorStatus !== "approved" && (
            <>
              <AccountMenuLink
                to="/account/become-creator"
                onNavigate={() => setOpen(false)}
              >
                {creatorStatus === "applied"
                  ? "Your creator application"
                  : "Become a creator"}
              </AccountMenuLink>
              <div className="my-1 h-px bg-brand-gold-500/15" />
            </>
          )}
          <AccountMenuLink to="/account/profile" onNavigate={() => setOpen(false)}>
            Profile
          </AccountMenuLink>
          <AccountMenuLink
            to="/account/preferences"
            onNavigate={() => setOpen(false)}
          >
            Preferences
          </AccountMenuLink>
          <AccountMenuLink to="/account/wallet" onNavigate={() => setOpen(false)}>
            Wallet
          </AccountMenuLink>
          <AccountMenuLink to="/account/gdpr" onNavigate={() => setOpen(false)}>
            Privacy &amp; data
          </AccountMenuLink>
          <div className="my-1 h-px bg-brand-gold-500/15" />
          {email ? (
            <div
              className="px-4 pb-1 pt-1"
              data-testid="account-menu-signed-in-as"
            >
              <p className="text-[11px] text-brand-paper-100/50">
                Signed in as
              </p>
              <p className="break-all font-mono text-xs text-brand-paper-100/70">
                {email}
              </p>
            </div>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            disabled={logout.isPending}
            className="block w-full px-4 py-2 text-left text-sm text-brand-paper-100/90 transition-colors hover:bg-brand-forest-700 disabled:opacity-60"
            data-testid="account-menu-signout"
          >
            {logout.isPending ? "Signing out…" : "Sign out"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AccountMenuLink({
  to,
  onNavigate,
  children,
}: {
  to: string;
  onNavigate: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onNavigate}
      className="block px-4 py-2 text-sm text-brand-paper-100/90 transition-colors hover:bg-brand-forest-700"
    >
      {children}
    </Link>
  );
}
